<template>
  <div class="FirmwareSettingsDetails">
    <section v-if="items.length === 0" class="firmwareSettingsDetails_loading">
      <v-progress-circular
        :size="30"
        :width="2"
        indeterminate
      ></v-progress-circular>
    </section>
    <main v-if="recordSetting">
      <section class="firmwareSettingsDetails_left">
        <h3 class="firmwareSettingsDetails_left-title">カメラ設定</h3>
        <h3
          v-for="(item, index) in items"
          v-show="!item.hide"
          :key="index"
          :class="{ 'firmwareSettingsDetails_left--disabled': item.disabled }"
        >
          <span>
            {{ item.label }}
            <em v-if="item.desc">{{ item.desc }}</em>
          </span>
          <span
            v-if="item.type === 'switch'"
            class="firmwareSettingsDetails_switch-wrapper"
          >
            <v-switch
              v-model="item.switch"
              :ripple="false"
              :disabled="item.disabled"
              dense
              @click.stop
              @change="handleSwitch($event, item, index)"
            ></v-switch>
          </span>
          <span
            v-if="item.type === 'link' && !item.disabled"
            v-ripple
            class="firmwareSettingsDetails_link"
            @click="handleLink(item.name)"
          >
            {{ item.linkText }}
            <v-icon class="firmwareSettingsDetails_link-icon" size="20">
              mdi-chevron-right
            </v-icon>
          </span>
          <span
            v-if="item.type === 'tab' && !item.disabled"
            class="firmwareSettingsDetails_tabs"
          >
            <app-tabs
              v-model="item.tab"
              :tab-items="item.items"
              @onChange="handleTabChange($event, item.name)"
            />
          </span>
        </h3>
        <slot v-if="canConfigCamera" name="append"></slot>
      </section>
    </main>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
const linkEnum = {
  OFF: '常時無効',
  ON: '常時有効',
  AUTO: '自動',
  NA: '非活性',
};
export default {
  name: 'FirmwareSettingsDetails',
  props: {
    routeFrom: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      switchCams: {},
      displayCam: null,
      allSelected: false,
      isChanged: false,
      submitData: {},
      items: [
        {
          key: 'motion',
          label: '動体検知設定',
          linkText: 'OFF',
          type: 'link',
          disabled: false,
          name: 'settings-firmware-motion',
        },
        {
          label: '低遅延モード',
          switch: false,
          type: 'switch',
          hide: true,
        },
      ],
    };
  },
  computed: {
    ...mapState({
      newUserCamList: (state) => state.settings.newUserCamList,
      camRecordInfo: (state) => state.settings.camRecordInfo,
      cameraById: (state) => state.common.cameraById,
      currentCameraPolicy: (state) => state.common.currentCameraPolicy,
      isCurrentAvatarOwner: (state) => state.settings.isCurrentAvatarOwner,
    }),
    canConfigCamera() {
      return (
        this.isCurrentAvatarOwner || this.selectedCam?.cameraPolicy?.access
      );
    },
    selectedCam() {
      return this.cameraById[this.id];
    },
    recordSetting() {
      return this.camRecordInfo[String(this.id)];
    },
    id() {
      return this.$route.params.id;
    },
  },
  watch: {
    recordSetting: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val) {
          this.mapItems(val);
        }
      },
    },
    canConfigCamera: {
      immediate: true,
      handler(val) {
        const items = [
          {
            key: 'motion',
            label: '動体検知設定',
            linkText: 'OFF',
            type: 'link',
            disabled: false,
            name: 'settings-firmware-motion',
          },
          {
            label: '低遅延モード',
            switch: false,
            type: 'switch',
            hide: true,
          },
        ];

        if (val) {
          items.push(
            {
              key: 'video',
              label: '録画とライブ',
              switch: false,
              type: 'switch',
            },
            {
              key: 'audio',
              label: '録音',
              switch: false,
              type: 'switch',
              hide: !this.selectedCam?.capabilities?.audioRecording,
            },
            {
              key: 'displaytime',
              label: '時刻表示',
              switch: false,
              type: 'switch',
            },
            {
              key: 'wdr',
              label: 'WDR',
              desc: '（ワイドダイナミックレンジ）',
              switch: false,
              type: 'switch',
              value: 'NA',
              disabled: false,
            },
            {
              label: '夜間モード',
              linkText: '',
              type: 'link',
              value: 'NA',
              disabled: true,
              name: 'settings-firmware-mode',
            },
            {
              label: '電源周波数',
              linkText: '',
              type: 'tab',
              value: 'NA',
              tab: -1,
              items: [
                { value: '50', label: '50Hz' },
                { value: '60', label: '60Hz' },
              ],
              name: 'settings-powerfrequency',
              hide: true,
            }
          );
          this.items = items;
        }
        this.mapItems(this.recordSetting);
      },
    },
  },
  methods: {
    ...mapActions('common', ['setIsLoading']),
    ...mapActions('settings', ['setNewUserCamList', 'updateRecordSettings']),
    ...mapActions('video', ['setLowLatencyModeById']),
    async handleTabChange(event, name) {
      const newValue = event === 0 ? '50' : '60';
      if (
        name === 'settings-powerfrequency' &&
        this.recordSetting?.powerfrequency !== newValue
      ) {
        try {
          this.setIsLoading(true);
          // 50Hz: 0, 60Hz: 1
          await this.updateRecordSettings({
            data: {
              powerfrequency: newValue,
            },
            cameraId: this.id,
          });
        } catch (error) {
          this.updatePowerFrequecyItem(
            this.items,
            this.recordSetting?.powerfrequency
          );
          console.error(error);
        } finally {
          this.setIsLoading(false);
        }
      }
    },
    disableLowLatencyMode() {
      this.$set(this.items[1], 'switch', false);
    },
    mapFromRecordSettings(refItems, recordSettings) {
      // motionSettings
      refItems[0] &&
        this.$set(
          refItems[0],
          'linkText',
          recordSettings?.motionSettings?.value?.enable ? 'ON' : 'OFF'
        );
      // low latency mode
      this.$set(
        refItems[1],
        'switch',
        this.cameraById[this.id].latencyMode === 'WebRTC'
      );
      // record video
      refItems[2] &&
        this.$set(refItems[2], 'switch', recordSettings?.video === 1);
      // record audio
      refItems[3] &&
        this.$set(refItems[3], 'switch', recordSettings?.audio === 1);
      // displaytime
      refItems[4] &&
        this.$set(refItems[4], 'switch', recordSettings?.displaytime === 1);

      // wdr mode
      if (refItems[5]) {
        this.$set(
          refItems[5],
          'switch',
          recordSettings?.wdr === 'ON' && recordSettings?.wdr !== 'NA'
        );
        this.$set(refItems[5], 'value', recordSettings?.wdr || 'NA');
        this.$set(refItems[5], 'disabled', recordSettings?.wdr === 'NA');
      }

      // daynight mode
      if (refItems[6]) {
        let daynight = recordSettings?.daynight;
        this.$set(refItems[6], 'linkText', daynight ? linkEnum[daynight] : '');
        this.$set(refItems[6], 'value', daynight || 'NA');
        this.$set(
          refItems[6],
          'disabled',
          daynight === 'NA' || daynight === undefined
        );
      }

      // power frequency
      this.updatePowerFrequecyItem(refItems, recordSettings?.powerfrequency);
    },
    updatePowerFrequecyItem(refItems, powerfrequency) {
      if (refItems[7]) {
        const isEmpty = powerfrequency === null || powerfrequency === '';
        this.$set(refItems[7], 'value', powerfrequency);
        this.$set(
          refItems[7],
          'tab',
          isEmpty ? -1 : powerfrequency === '50' ? 0 : 1
        ); // 50Hz: 0, 60Hz: 1
        this.$set(refItems[7], 'hide', powerfrequency === 'NA');
      }
    },
    mapItems(recordSettings) {
      if (recordSettings && this.items.length) {
        this.mapFromRecordSettings(this.items, recordSettings);
      }
    },
    reconnectCam() {
      this.$emit('reconnectCam');
    },
    handleLink(name) {
      this.routerPush({
        name,
        params: this.$route.params,
      });
    },
    async handleSwitch(ev, item, index) {
      if (index === 1) {
        const latencyMode = ev ? 'WebRTC' : 'HLS';
        await this.setLowLatencyModeById({
          id: this.id,
          latencyMode: latencyMode,
        });
        this.reconnectCam();
      }
      if (index === 5) {
        this.$set(item, 'value', ev ? 'ON' : 'OFF');
      }
      if (item.key) {
        try {
          this.setIsLoading(true);
          await this.updateRecordSettings({
            data: {
              [item.key]: index === 5 ? (ev ? 'ON' : 'OFF') : ev ? 1 : 0,
            },
            cameraId: this.id,
          });
          this.reconnectCam();
        } catch (error) {
          console.error();
        } finally {
          this.setIsLoading(false);
        }
      }
    },
  },
};
</script>

<style lang="scss">
.firmwareSettingsDetails {
  position: relative;

  &_switch-wrapper {
    width: 55px;
    text-align: center;
    .v-input.v-input--switch {
      margin-top: 0;
      padding-top: 0;
      display: inline-block;
      &.v-input--is-disabled {
        opacity: 0.4;
      }
      .v-input__control {
        .v-messages {
          display: none;
        }

        .v-input__slot {
          margin-bottom: 0;

          .v-input--selection-controls__input {
            .v-input--switch__track {
              border-radius: 15px;
              width: 42px;
              height: 22px;
              left: -1px;
              position: absolute;
              opacity: 1;
              right: 2px;
              top: calc(50% - 11px);
              color: #cccccc !important;
            }

            .v-input--switch__track.primary--text {
              color: #0075c2 !important;
            }

            .v-input--switch__thumb {
              color: #ffffff !important;
              width: 20px;
              height: 20px;
              box-shadow: -3px 0 3px 0 rgba(0, 0, 0, 0.1);
            }
          }

          .theme--light.v-label {
            font-size: 12px;
            font-weight: 500;
            font-stretch: normal;
            font-style: normal;
            letter-spacing: 0.6px;
            color: #666;
          }
        }
      }
    }
  }
  &_loading {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;
  }

  &_flex-gap {
    display: flex;
    flex: 1;
  }
  &_divider {
    &.theme--light.v-divider {
      border-color: #ccc;
    }
  }

  &_link {
    font-size: 14px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    text-align: right;
    color: #989898;
    margin-left: 7px;
    cursor: pointer;
    &-icon {
      &.v-icon.v-icon {
        color: #c1c1c1;
        margin-top: -2px;
      }
    }
    &--disabled {
      opacity: 0.4;
    }
  }

  main {
    display: flex;
    flex-direction: row;
    margin-bottom: 10px;
  }
  &_left {
    width: 100%;
    .customCheck {
      padding: 12px 10px;
      &:not(:last-child) {
        border-bottom: 1px solid #eee;
      }
      .customCheck_label {
        width: 400px;
      }
    }
    & h3 {
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
      text-align: left;
      color: #666;
      &:not(:first-child) {
        display: flex;
        align-items: center;
      }
      span {
        &:first-child {
          width: 200px;
        }
        em {
          font-size: 12px;
          font-weight: normal;
          font-stretch: normal;
          font-style: normal;
          line-height: 1.75;
          letter-spacing: normal;
          text-align: left;
          color: #4b4b4b;
        }
      }
      &.firmwareSettingsDetails_left--disabled {
        span {
          &:first-child {
            opacity: 0.4;
          }
          em {
            opacity: 0.7;
          }
        }
      }
    }
    &-title {
      margin-top: 20px;
    }
  }
  &_righ {
    margin-top: 11px;
    padding: 0 10px;
    .firmwareSettingsDetails_cam {
      position: relative;
    }
    h5 {
      margin-top: 5px;
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
  }
}
</style>
